/**
 * Posições · Distribuição (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_distribuicao.py).
 *
 * Todas as carteiras lado a lado. Na URL: officers (vazio = todos). Carteiras filtradas
 * por `portfolios_visiveis` no servidor. Emissores, target e posições por ativo ficam
 * só no snapshot para IA.
 */
import { Alert, Button, Loader, MultiSelect, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, montarUrl, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/distribuicao';

interface Dados {
  opcoes: { officers: string[] };
  filtro: string;
  n_portfolios: number;
  aviso?: string;
  por_classe?: TabelaApi;
}

export function Distribuicao() {
  const [filtros, definir] = useFiltrosUrl({ officers: '' });
  const officers = filtros.officers ? filtros.officers.split(',') : [];
  const parametros = { officers };
  const consulta = useQuery({
    queryKey: ['carteiras.distribuicao', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<Dados>>(CAMINHO, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const d = consulta.data?.data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Distribuição"
        descricao="Percentual do patrimônio de cada carteira em cada classe de ativo."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Fibery (Posição Consolidada, Ativos, Política, Portfolio)"
        acoes={
          d && !d.aviso ? (
            <Button
              component="a"
              variant="light"
              color="dourado"
              href={montarUrl(`${CAMINHO}/snapshot`, { officers })}
            >
              Snapshot para IA (JSON)
            </Button>
          ) : undefined
        }
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Filtros">
          <MultiSelect
            label="Officer"
            placeholder={officers.length ? '' : 'Todos'}
            data={d?.opcoes.officers ?? []}
            value={officers}
            onChange={(v) => definir({ officers: v.join(',') })}
            searchable
            clearable
            w={LARGURA.selecao + 40}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar as posições.'}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {d && (
        <Text size="sm" c="dimmed">
          {d.n_portfolios} portfolios · filtro: {d.filtro}
        </Text>
      )}
      {d?.aviso && <Alert color="yellow">{d.aviso}</Alert>}
      {d?.por_classe && (
        <Secao
          titulo="Distribuição por classe"
          legenda="% do patrimônio. Uma linha por carteira."
        >
          <TabelaDados tabela={d.por_classe} altura={640} fixarPrimeira />
        </Secao>
      )}
    </Pagina>
  );
}
